import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ICreateVoiceAgentFormData } from '@/@types/voiceAgents';

interface IAgentFormAiTabProps {
  // LOCAL_TYPE_JUSTIFIED: single-use props interface for this tab sub-component
  form: ICreateVoiceAgentFormData;
  setField: (field: string, value: string | number | boolean | string[] | undefined) => void;
}

// LiveKit Inference models — prices from LiveKit Cloud (Build plan, April 2026)
// No separate API keys needed — billed through LiveKit Cloud
const LLM_MODELS = [
  { value: 'openai/gpt-4o-mini', label: 'GPT-4o Mini — $0.15/1M in' },
  { value: 'openai/gpt-4o', label: 'GPT-4o — $2.50/1M in' },
  { value: 'openai/gpt-4.1-nano', label: 'GPT-4.1 Nano — $0.10/1M in' },
  { value: 'openai/gpt-4.1-mini', label: 'GPT-4.1 Mini — $0.40/1M in' },
  { value: 'openai/gpt-4.1', label: 'GPT-4.1 — $2.00/1M in' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini — $0.10/1M in' },
  { value: 'openai/gpt-5', label: 'GPT-5 — $0.50/1M in' },
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash — $0.15/1M in' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro — $1.25/1M in' },
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash — $0.10/1M in' },
  { value: 'deepseek-ai/deepseek-v3', label: 'DeepSeek V3 — $0.77/1M in' },
  { value: 'openai/gpt-oss-120b', label: 'GPT OSS 120B — $0.10/1M in' },
];

const STT_PROVIDERS = [
  { value: 'deepgram/nova-3', label: 'Deepgram Nova-3 — $0.0077/min' },
  { value: 'deepgram/nova-3-multi', label: 'Deepgram Nova-3 Multi — $0.0092/min' },
  { value: 'deepgram/nova-2', label: 'Deepgram Nova-2 — $0.0058/min' },
  { value: 'deepgram/nova-2-phonecall', label: 'Deepgram Nova-2 Phone — $0.0058/min' },
  { value: 'deepgram/flux-general-en', label: 'Deepgram Flux — $0.0077/min' },
  { value: 'cartesia/ink-whisper', label: 'Cartesia Ink Whisper — $0.003/min' },
  { value: 'elevenlabs/scribe_v2_realtime', label: 'ElevenLabs Scribe v2 — $0.0105/min' },
];

const TTS_PROVIDERS = [
  { value: 'cartesia/sonic-3', label: 'Cartesia Sonic 3 — $50/1M chars' },
  { value: 'cartesia/sonic', label: 'Cartesia Sonic — $50/1M chars' },
  { value: 'cartesia/sonic-turbo', label: 'Cartesia Sonic Turbo — $50/1M chars' },
  { value: 'deepgram/aura-2', label: 'Deepgram Aura-2 — $30/1M chars' },
  { value: 'elevenlabs/eleven_turbo_v2_5', label: 'ElevenLabs Turbo v2.5 — $150/1M chars' },
  { value: 'elevenlabs/eleven_flash_v2_5', label: 'ElevenLabs Flash v2.5 — $150/1M chars' },
  { value: 'rime/mistv3', label: 'Rime Mist v3 — $30/1M chars' },
  { value: 'inworld/inworld-tts-1', label: 'Inworld TTS 1 — $5/1M chars' },
  { value: 'xai/tts-1', label: 'xAI TTS — $4.20/1M chars' },
];

const NONE_VALUE = '__none__';

export function AgentFormAiTab({ form, setField }: IAgentFormAiTabProps) {
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="llm-model">LLM Model</Label>
          <Select value={form.llmModel} onValueChange={v => setField('llmModel', v)}>
            <SelectTrigger id="llm-model" className="w-full">
              <SelectValue placeholder="Select LLM" />
            </SelectTrigger>
            <SelectContent>
              {LLM_MODELS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="llm-fallback">LLM Fallback</Label>
          <Select
            value={form.llmFallback ?? NONE_VALUE}
            onValueChange={v => setField('llmFallback', v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id="llm-fallback" className="w-full">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {LLM_MODELS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="stt-provider">STT Provider</Label>
          <Select
            value={form.sttProvider ?? NONE_VALUE}
            onValueChange={v => setField('sttProvider', v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id="stt-provider" className="w-full">
              <SelectValue placeholder="Default (Deepgram)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>Default (Deepgram Nova-3)</SelectItem>
              {STT_PROVIDERS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="stt-fallback">STT Fallback</Label>
          <Select
            value={form.sttFallback ?? NONE_VALUE}
            onValueChange={v => setField('sttFallback', v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id="stt-fallback" className="w-full">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {STT_PROVIDERS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="tts-provider">TTS Provider</Label>
          <Select
            value={form.ttsProvider ?? NONE_VALUE}
            onValueChange={v => setField('ttsProvider', v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id="tts-provider" className="w-full">
              <SelectValue placeholder="Default (Cartesia)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>Default (Cartesia Sonic-3)</SelectItem>
              {TTS_PROVIDERS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="tts-fallback">TTS Fallback</Label>
          <Select
            value={form.ttsFallback ?? NONE_VALUE}
            onValueChange={v => setField('ttsFallback', v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id="tts-fallback" className="w-full">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>None</SelectItem>
              {TTS_PROVIDERS.map(m => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="voice-id">Voice ID</Label>
        <Input
          id="voice-id"
          value={form.voiceId ?? ''}
          onChange={e => setField('voiceId', e.target.value || undefined)}
          placeholder="Provider-specific voice ID (optional)"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="turn-detection">Turn Detection Sensitivity</Label>
        <Select
          value={form.turnDetectionSensitivity}
          onValueChange={v => setField('turnDetectionSensitivity', v)}
        >
          <SelectTrigger id="turn-detection" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low — longer pauses before responding</SelectItem>
            <SelectItem value="medium">Medium — balanced</SelectItem>
            <SelectItem value="high">High — faster responses</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="preemptive">Preemptive Generation</Label>
        <Switch
          id="preemptive"
          checked={form.preemptiveGeneration}
          onCheckedChange={v => setField('preemptiveGeneration', v)}
        />
      </div>
    </div>
  );
}
